import styled, { keyframes } from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import AtLogo from "../../assets/at-logo.svg";

// at logo with navigation to startpage
interface BurgerLogoProps {
  $isScrolled: boolean;
}

const ATLogoBurger = styled.img<BurgerLogoProps>`
  align-self: flex-start;
  width: ${(props) => (props.$isScrolled ? "40px" : "60px")};
  margin: 24px 10px;
  transition: width 0.3s ease;

  @media (min-width: 768px) {
    margin: 24px 24px;
  }

  @media (min-width: 1024px) {
    display: none;
  }
`;

// Logo and navigation to startpage
interface AtBurgerLogoProps {
  $isScrolled: boolean;
  style?: React.CSSProperties;
}

export const AtLogoMobile: React.FC<AtBurgerLogoProps> = ({ $isScrolled }) => {
  const navigate = useNavigate();

  const scrollToTop = () => {
    navigate("/");
    window.scrollTo(0, 0);
  };
  return (
    <Link to="/" onClick={scrollToTop}>
      <ATLogoBurger src={AtLogo} alt="Andtech logo" $isScrolled={$isScrolled} />
    </Link>
  );
};

// menu container for hamburger menu
interface BurgerMenuProps {
  $isScrolled: boolean;
}

export const BurgerMenuContainer = styled.div<BurgerMenuProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px 0 20px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  background-color: ${(props) =>
    props.$isScrolled ? "var(--background)" : "transparent"};
  transition: background-color 0.3s ease;

  /* Desktop */
  @media (min-width: 1024px) {
    display: none;
  }
`;

// cross icon for closing hamburger menu
interface CrossDivProps {
  open: boolean;
}

export const CrossDiv = styled.div<CrossDivProps>`
  position: absolute;
  top: 24px;
  right: 8px;
  display: ${({ open }) => (open ? "flex" : "none")};
  justify-content: flex-end;
  padding: 10px;
  z-index: 1003;
`;

export const CrossIcon = styled.div`
  width: 30px;
  height: 30px;
  position: relative;
  display: flex;
  justify-content: flex-end;

  cursor: pointer;
  z-index: 1002;

  span {
    position: absolute;
    width: 30px;
    height: 2px;
    background-color: var(--maindark);
    border-radius: 1px;
    transition: all 0.3s;
  }

  span:nth-child(1) {
    transform: rotate(45deg);
  }
  span:nth-child(2) {
    transform: rotate(-45deg);
  }
`;

// Menu button for hamburger menu
const ButtonWave = keyframes`
  0%, 100% { 
    transform: translateY(0); 
  }
  50% { 
    transform: translateY(-8px); 
  }
`;

const Letter = styled.span`
  display: inline-block;
`;

const BurgerMenuButton = styled.button`
	font-size: 14px;
	font-weight: 700;
	text-transform: uppercase;
	background-color: transparent;
	color: var(--maindark);
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 5px;
	padding: 10px 18px;
	cursor: pointer;
	border: none;
	position: relative;
	z-index: 100;
	margin-right: 18px;

  &:hover {
    transform: scale(1.04);
    transition: transform 0.2s ease-in-out;
    background-color: transparent;
    color: var(--bg-pink);
  }

	&:hover ${Letter} {
 		animation: ${ButtonWave} 0.6s ease-in-out forwards;
	}

	@media (min-width: 768px) {
		margin-right: 28px;
	}
 `;

interface NavButtonProps {
	onClick?: () => void
	"aria-expanded"?: boolean
	"aria-controls"?: string
}

export const NavButton: React.FC<NavButtonProps> = ({ onClick }) => {
	const menuText = "MENY";

	return (
		<BurgerMenuButton onClick={onClick}>
			{menuText.split("").map((char, i) => (
				<Letter key={i} style={{ animationDelay: `${i * 0.1}s` }}>
					{char}
				</Letter>
			))}
		</BurgerMenuButton>
	)
};

// navigation menu for hamburger menu
export const Menu = styled.nav<{ open: boolean }>`
  position: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 180px;
  top: 0;
  right: 0;
  height: 100vh;
  width: 100%;
  background-color: var(--mainlight);
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
  transform: ${({ open }) => (open ? "translateX(0)" : "translateX(100%)")};
  transition: transform 0.3s ease-in-out;
  z-index: 999;

  ul {
    list-style: none;
    margin: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;

    li {
      margin: 10px;
    }
  }

  @media (min-width: 768px) {
    width: 70%;
  }
`;

interface BurgerMenuLinksProps {
  to?: string;
}

export const BurgerMenuLinks = styled(Link)<BurgerMenuLinksProps>`
  color: var(--maindark);
  font-weight: 400;
  font-size: 18px;
  text-transform: uppercase;
  letter-spacing: 2px;
  position: relative;
  width: fit-content;
  margin: 0 0 1rem 0;
  text-decoration: none;

  &::after {
    content: "";
    position: absolute;
    width: 0;
    height: 3px;
    bottom: -3px;
    left: 0;
    background-color: var(--bg-pink);
    transform-origin: left;
    transition: width 0.5s ease;
    visibility: hidden;
  }

  &:hover::after {
    visibility: visible;
    width: 100%;
  }
`;

// contact button for hamburger menu
interface MenuContactButtonProps {
  to: string;
  closeMenu: () => void;
}

const MenuButton = styled.button`
  border-radius: 50px;
  border: 2px solid var(--bg-pink);
  color: var(--bg-pink);
  background-color: transparent;
  text-transform: uppercase;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 2px;
  text-decoration: none;
  cursor: pointer;
  padding: 10px 16px;
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.02);
    border: 2px solid var(--accent-pink);
    color: var(--accent-pink);
  }

  &:active {
    transform: scale(0.9);
    border: 2px solid var(--maindark);
    color: var(--maindark);
  }
`;

export const MenuContactButton: React.FC<MenuContactButtonProps> = ({
  to,
  closeMenu,
}) => {
  const navigate = useNavigate();

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.preventDefault();
    closeMenu();
    navigate(to);
  };

  return <MenuButton onClick={handleClick}>Kontakt</MenuButton>;
};
